import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AppState } from '@app/store/state/app.state';
import { getAllUsersRequest } from '@features/settings/services/UserService';
import { getAllUserTags } from '@shared/services/Tags';
import { SimpleUser } from "@shared/models/user.models";
import { UserTag } from '@shared/models/tags.models';
import { addOrReplaceQueryParam } from "@shared/utils/UrlUtils";

export enum UserManagementView {
  TagManager = 'tag', UserManager = 'user',
}

export interface UserManagementState {
  loading: boolean;
  activeView: UserManagementView;
  allUsers: SimpleUser[];
  filteredUsers: SimpleUser[];
  allUserTags: UserTag[];
}

export const initialUserManagementState: UserManagementState = {
  loading: false,
  activeView: UserManagementView.UserManager,
  allUsers: [],
  filteredUsers: [],
  allUserTags: [],
};

export const setActiveView: any = createAsyncThunk(
  'userManagement/setActiveView',
  async (view: UserManagementView, thunkAPI) => {
    addOrReplaceQueryParam('view', view);
    return view;
  }
);

export const loadAllUsers: any = createAsyncThunk(
  'userManagement/loadAllUsers',
  async (skip: number = 0, thunkAPI) => {
    const allUsers = await getAllUsersRequest();
    return allUsers;
  }
);

export const loadAllUserTags: any = createAsyncThunk(
  'userManagement/loadAllUserTags',
  async (skip: number = 0, thunkAPI) => {
    const allUserTags = await getAllUserTags();
    return allUserTags;
  }
);

const UserManagementSlice = createSlice({
  name: 'UserManagement',
  initialState: initialUserManagementState,
  reducers: {
    resetUserManagement: (state) => (initialUserManagementState),
    updateFilteredUsers: (state, action) => {
      const filteredUsers = action.payload.length > 2 ? state.filteredUsers.filter(u => `${u.firstName} ${u.lastName} ${u.email} ${u.teamName}`.toLowerCase().includes(action.payload.toLowerCase())) : state.allUsers;
      return {
        ...state, filteredUsers: filteredUsers,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAllUsers.pending, (state) => ({...state, loading: true}))
      .addCase(loadAllUsers.rejected, (state) => ({...state, loading: false}))
      .addCase(loadAllUsers.fulfilled, (state, action) => ({...state, allUsers: action.payload, filteredUsers: action.payload, loading: false}))
      .addCase(setActiveView.fulfilled, (state, action) => ({...state, activeView: action.payload}))
      .addCase(loadAllUserTags.fulfilled, (state, action) => ({...state, allUserTags: action.payload}));
  },
});



export default UserManagementSlice.reducer;
export const { updateFilteredUsers } = UserManagementSlice.actions;


// Selectors
export const selectFilteredUsers = (state: AppState) => state.userManagement.filteredUsers;
// The unfiltered list, for callers that look users up by id rather than showing the search results.
export const selectAllUsers = (state: AppState) => state.userManagement.allUsers;
export const selectAllUserTags = (state: AppState) => state.userManagement.allUserTags;
